<template>
    <div>
		<landing-page-navbar></landing-page-navbar>

        <!-- Loading state -->
        <div class="mt-5 pt-5" v-if="loading && !showForm">
            <loading-state class="mt-5"></loading-state>
            <h4 class="mb-4 text-center">Loading your booking details</h4>
        </div>

        <div class="booking-view">
            <div class="content-container">
                <div class="content h-auto">
                    <!-- Order not found -->
                    <div class="container" v-if="showForm || (!order && order.details)">
                        <div class="card">
                            <div class="card-body pb-0">
                                <div class="alert alert-danger mb-0" v-if="error.messages.length > 0">
                                    <ul class="mb-0">
                                        <li v-for="(error, index) in error.messages" :key="index">
                                            {{ error }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="card-header">
                                <h4>Track your technician</h4>
                            </div>
                            <div class="card-body">
                                <div class="form-group">
                                    <label>Enter your email address</label>
                                    <input type="email" class="form-control" v-model="form.email" placeholder="email@address.com" :class="{'is-invalid': error.email}" v-on:keyup.enter="findOrder()">
                                    <div class="invalid-feedback" v-if="error.email">Your email address is missing</div>
                                </div>
                                <div class="form-group">
                                    <label>
                                        Enter your order reference &nbsp;
                                        <div class="d-sm-none">yes</div>
                                        <span class="text-link text-sm cursor-pointer"  v-b-popover.hover.top="'When you placed an order with us, you will have received a booking confirmation email, which contains your order reference.'">Where do I find this?</span>
                                    </label>
                                    <input type="text" class="form-control" inputmode="numeric" pattern="[0-9]*" v-model="form.orderRef" placeholder="e.g. 123456" :class="{'is-invalid': error.orderRef}" v-on:keyup.enter="findOrder()">
                                    <div class="invalid-feedback" v-if="error.orderRef">Your order ID is missing</div>
                                </div>
                                <button class="btn continue-btn btn-block btn-lg" @click="findOrder()">Find order</button>
                            </div>
                        </div>
                    </div>

                    <div class="container" v-if="order && !loading">

                        <!-- Order can't be found -->
                        <div class="card card-body" v-if="!order && !order.details">
                            <empty-state
                                statusIcon="error"
                                title="Sorry, we couldn't find that order"
                                :subtitle="`Please call us on ${carrierDetails.phone} if you think there is an issue`"
                            ></empty-state>
                        </div>

                        <!-- Order complete -->
                        <div class="card card-body" v-if="!order.details.current_eta && order.details.complete">
                            <empty-state
                                statusIcon="success"
                                title="The technician has completed your repair!"
                                :subtitle="`If you need to get in touch please call us on: ${carrierDetails.phone}`"
                            ></empty-state>
                        </div>

                        <template v-if="!order.details.complete">

                            <!-- Current progress -->
                            <div class="card card-body">
                                <span v-if="order.details.current_eta">
                                    <h5>The technician is on their way to you now, their current arrival time is:</h5>
                                    <h3 class="mb-0"><strong>{{ order.details.current_eta }}</strong></h3>
                                </span>
                                <span v-else>
                                    <h5 class="mb-0"><strong>The technician is not heading to your location just yet</strong></h5>
                                </span>
                            </div>

                            <!-- Map -->
                            <div class="card" v-if="showMap">
                                <gmap-map
                                    :center="center"
                                    class="map-container"
                                    ref="map"
                                    :zoom="8"
                                >
                                    <GmapMarker
                                        :position="currentLocation.position"
                                        :clickable="true"
                                        :draggable="true"
                                        :icon="{ url: ('https://s3.eu-west-2.amazonaws.com/wefixuk/images/icons/van-map-marker.png')}"
                                        @click="center=currentLocation.position"
                                    />
                                    <GmapMarker
                                        :position="repairLocation.position"
                                        :clickable="true"
                                        :draggable="true"
                                        @click="center=repairLocation.position"
                                    />
                                </gmap-map>
                            </div>

                            <!-- Repair details -->
                            <div class="card">
                                <div class="card-header mb-0 pb-0">
                                    <h4>Repair details</h4>
                                </div>
                                <div class="card-body pb-2">
                                    <dl>
                                        <dt>Arrival timeslot</dt>
                                        <dd v-if="order.details.sameday_booking !== 'sameday'">{{ getHour(order.details.arrival_time_start) }} - {{ getHour(order.details.arrival_time_end) }}</dd>
                                        <dd v-else>{{ order.details.arrival_start }} - {{ order.details.arrival_end }}</dd>
                                    </dl>
                                    <dl>
                                        <dt>Repair address</dt>
                                        <dd>{{ order.details.address_string }}</dd>
                                    </dl>
                                </div>
                            </div>

                            <!-- Devices to be repaired -->
                            <div class="card" v-if="orderDevices.length > 0">
                                <div class="card-header">
                                    <h4>Devices to be repaired</h4>
                                </div>
                                <div class="card-body pb-2">
                                    <div class="media mb-3" v-for="(device, index) in orderDevices" :key="index">
                                        <div class="media-left d-none d-md-block mr-2" v-if="device.model_version">
                                            <img :src="device.model_version.image.path" alt="Device Image" class="img-2x" />
                                        </div>
                                        <div class="media-body">
                                            <h5 v-if="device.model_version">{{ device.model_version.image.title }}</h5>
                                            <div class="price-table table">
                                                <div class="pricing-container">
                                                    <div class="tr" v-for="(repair, index) in device.details" :key="index">
                                                        <div class="td">
                                                            {{ repair.problem.title }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </template>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex"

export default {

    data() {
        return {
            center: null,
            currentLocation: [],
            error: {
                email: false,
                messages: [],
                orderRef: false,
            },
            form: {
                email: null,
                orderRef: null,
            },
            loading: true,
            repairLocation: [],
            reloadOrderInterval:null,
            reloadVanInterval:null,
            showForm: false,
            showMap: false
        }
    },

    props:['email', 'orderRef'],

    mounted() {
        this.loadOrder();
        this.reloadData();
    },

    created() {
        this.loadOrder();
        this.reloadData();
    },

    beforeDestroy () {
        clearInterval(this.reloadVanInterval);
        clearInterval(this.reloadOrderInterval);
    },

    computed: {
        ...mapGetters([
            'carrierDetails',
            'order',
            'orderDevices'
        ])
    },

    methods: {
        ...mapActions([
            'retrieveOrderDevices',
            'retrieveOrder',
            'retrieveTrackingVan'
        ]),

        getHour(dateString) {
            if (!dateString) {
                return "";
            }
            var t = dateString.split(/[- :]/);

            // Apply each element to the Date function
            var d = new Date(t[0], t[1]-1, t[2], t[3], t[4], t[5]);
            var date = new Date(d);

            var hours = date.getHours();
            var minutes = date.getUTCMinutes();

            if (hours <= 9) {
                hours = "0" + hours;
            }

            if (minutes <= 9) {
                minutes = "0" + minutes;
            }
            return hours + ':' + minutes;
        },

        /** Search for an order **/
        findOrder() {
            this.error.email = false;
            this.error.orderRef = false;
            this.error.messages = [];

            if (!this.form.email) {
                this.error.email = true;
            }

            if (!this.form.orderRef) {
                this.error.orderRef = true;
            }

            if (this.form.email && this.form.orderRef) {
                this.loadOrder();
            }
        },

        initMap() {
            let repairLocation = {
                lat: parseFloat(this.order.details.latitude),
                lng: parseFloat(this.order.details.longitude),
            };
            this.repairLocation = {position: repairLocation};

            if (this.order.trackingVan && this.order.trackingVan.latitude && this.order.trackingVan.longitude) {
                let currentLocation = {
                    lat: parseFloat(this.order.trackingVan.latitude),
                    lng: parseFloat(this.order.trackingVan.longitude),
                };
                this.currentLocation = {position: currentLocation};
            }

            this.center = repairLocation;
        },

        loadOrder() {
            this.loading = true;
            this.showForm = false;
            if (
                (this.$route.params.orderRef && this.$route.params.email) ||
                (this.form.orderRef && this.form.email)
            ) {
                let orderRef = (this.form.orderRef) ? this.form.orderRef : this.$route.params.orderRef;
                let email = (this.form.email) ? this.form.email : this.$route.params.email;

                this.retrieveOrder({
                    orderRef: orderRef,
                    email: email
                }).then(() => {
                    if (email !== this.order.details.email || orderRef != this.order.details.uuid) {
                        this.loading = false;
                        this.showForm = true;
                        this.error.messages.push("Sorry, we couldn't find a booking with that email and order ID");
						return;
					}
                    this.showForm = false;
                    this.loadingDevices = true;
                    this.loading = false;
                    this.retrieveOrderDevices({
                        orderRef: orderRef,
                        email: email
                    }).then(() => {
                        if (this.order.details.current_eta && !this.order.details.complete) {
                            this.retrieveTrackingVan({
                                jobId: this.order.details.job_id,
                                email: this.order.details.email
                            }).then(() => {
                                this.initMap();
                                this.showMap = true;
                            });
                        }
                        this.loading = false;
                    })
                }).catch(error => {
                    this.showForm = true;
                    this.error.messages.push(error.response.data.messages[0]);
                });
            } else {
                if (Object.keys(this.$route.params).length > 0 && (!this.$route.params.orderRef || !this.$route.params.email)) {
                    this.error.messages.push("Sorry, we couldn't find your booking. Enter your details below to try and find it");
                }
                this.loading = false;
                this.showForm = true;
            }
        },

        reloadData() {
            let self = this;
            this.reloadVanInterval = setInterval(function() {
                if (self.order.details.current_eta && !self.order.details.complete) {
                    self.retrieveTrackingVan({
                        jobId: self.order.details.job_id,
                        email: self.order.details.email
                    }).then(() => {
                        self.initMap();
                    });
                }
            }, 60000);

            this.reloadOrderInterval = setInterval(function() {
                self.retrieveOrder({
                    orderRef: self.orderRef,
                    email: self.email,
                });
            }, 120000);
        },
    }
}
</script>